import { useState } from 'react';
import { toastWarning } from '../../../utils/toastMethod';
import { getPriceListQrCodeAPI, getPriceListTemplateAPI } from '../../../services/shopsApi';
import { useAppSelector } from '../../../hook/useReduxHooks';
import { RootState } from '../../../store/store';
import PriceListPreview from '../PriceListPreview';
import useCheckMobileResolution from '../../../hook/useCheckMobileResolution';
import * as XLSX from 'xlsx';
import './style.css';

interface UploadPriceListProps {
  title?: string;
  companyId: string | undefined;
  shopId: string | undefined;
}

export default function UploadPriceList({ title, companyId, shopId }: UploadPriceListProps) {
  const isPriceListUploaded = useAppSelector((state: RootState) => state.priceList.isPriceListUploaded);
  const [show, setShow] = useState(false);
  const [data, setData] = useState<Record<string, any[]>>({});
  const isMobile = useCheckMobileResolution();

  const handleDownloadTemplate = async () => {
    await getPriceListTemplateAPI(companyId, shopId)
      .then((response) => {
        const url = window.URL.createObjectURL(response);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'template-pricelist.xlsx');
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(() => {
        toastWarning('Qualcosa è andato storto. Riprova!');
      });
  };

  const handleUploadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validExtensions = ['.xls', '.xlsx'];
    const fileName = file.name.toLowerCase();
    const isValid = validExtensions.some((ext) => fileName.endsWith(ext));
    if (!isValid) {
      toastWarning('Formato file non valido. Carica un file in formato Excel (.xls, .xlsx)');
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = (evt) => {
      const arrayBuffer = evt.target?.result as ArrayBuffer;
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });

      const sheetsData: Record<string, any[]> = {};

      workbook.SheetNames.forEach((sheetName) => {
        const worksheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
        sheetsData[sheetName] = json;
      });

      setData(sheetsData);
    };
    reader.readAsArrayBuffer(file);

    setShow(true);
    e.target.value = '';
  };

  const handleDownloadQrCode = async () => {
    await getPriceListQrCodeAPI(companyId, shopId)
      .then((response) => {
        const url = window.URL.createObjectURL(response);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'qrcode-pricelist.png');
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(() => {
        toastWarning('Qualcosa è andato storto. Riprova!');
      });
  };

  const handleGoToPriceList = () =>
    window.open(
      `${process.env.REACT_APP_REDIRECT_PRICE_LIST_URL}${shopId}/listino-prezzi?source=dashboard` as string,
      '_blank'
    );

  return (
    <>
      <div className="pricelist-container">
        <label className="pricelist-title">{title}</label>
        <div>
          Mostra il tuo listino prezzi comodamente tramite la piattaforma Buoni Sul Serio. Segui questi semplici passi:
          <ul>
            <li>
              Scarica il template in formato Excel premendo sul pulsante "Scarica Template" e compilalo con i tuoi
              prodotti. Potrai dividerli in categorie e sottocategorie, aggiungere un nome, una descrizione, delle note
              e un prezzo.
              <br />
              Puoi anche avere più listini per ognuno dei tuoi negozi. Per esempio 2 diversi menù per il tuo ristorante
              (es: menù in italiano e menù in inglese; piuttosto che menù pausa pranzo e menù cena).
            </li>
            <li>Carica il tuo template utilizzando il pulsante "Carica Listino".</li>
          </ul>
        </div>
        <div style={isMobile ? { display: 'flex', flexDirection: 'column', gap: '10px' } : {}}>
          <button
            className="btn btn-primary"
            style={{ width: '180px', marginRight: '10px' }}
            onClick={handleDownloadTemplate}
          >
            Scarica template
          </button>
          <label htmlFor="upload-pricelist" className="btn btn-primary" style={{ width: '180px', marginRight: '10px' }}>
            Carica listino prezzi
            <input
              id="upload-pricelist"
              type="file"
              accept=".xlsx, .xls"
              onChange={handleUploadFile}
              style={{ display: 'none' }}
            />
          </label>
          {isPriceListUploaded && (
            <>
              <button
                className="btn btn-primary"
                style={{ width: '180px', marginRight: '10px' }}
                onClick={handleDownloadQrCode}
              >
                Scarica QrCode
              </button>
              <button className="btn btn-primary" style={{ width: '180px' }} onClick={handleGoToPriceList}>
                Vai al listino prezzi
              </button>
            </>
          )}
        </div>
      </div>

      <PriceListPreview show={show} setShow={setShow} data={data} companyId={companyId} shopId={shopId} />
    </>
  );
}
